import { useCallback, RefObject } from 'react';

import { PanZoom } from 'panzoom';

/**
 * Хук для контроля границ PanZoom-контейнера с автоматическим центрированием
 *
 * @param panZoomRef - Ссылка на экземпляр PanZoom
 * @param containerRef - Ссылка на DOM-элемент контейнера
 * @returns Объект с методами:
 *   - checkBoundaries: Проверяет текущее положение и центрирует при необходимости
 *   - setupBoundaryCheck: Устанавливает обработчик событий для автоматической проверки
 */

interface PanZoomBoundaries {
  checkBoundaries: () => void;
  setupBoundaryCheck: () => () => void;
}

export const usePanZoomBoundaries = (
  panZoomRef: RefObject<PanZoom | null>,
  containerRef: RefObject<HTMLElement | null>,
): PanZoomBoundaries => {
  const checkBoundaries = useCallback(() => {
    const container = containerRef.current;
    const panZoom = panZoomRef.current;
    if (!container || !panZoom) return;

    const { scale } = panZoom.getTransform();
    const threshold = 0.95;
    const { offsetWidth, offsetHeight } = container;
    const scaledWidth = offsetWidth * scale;
    const scaledHeight = offsetHeight * scale;

    const rect = container.getBoundingClientRect();
    const overflow = {
      left: Math.max(0, -rect.left) / scaledWidth,
      right: Math.max(0, rect.right - window.innerWidth) / scaledWidth,
      top: Math.max(0, -rect.top) / scaledHeight,
      bottom: Math.max(0, rect.bottom - window.innerHeight) / scaledHeight,
    };

    const shouldCenter = Object.values(overflow).some((v) => v > threshold);

    if (shouldCenter) {
      panZoom.pause();
      panZoom.smoothMoveTo(
        (offsetWidth - scaledWidth) / 2,
        (offsetHeight - scaledHeight) / 2,
      );

      setTimeout(() => panZoom.isPaused() && panZoom.resume(), 500);
    }
  }, [containerRef, panZoomRef]);

  const setupBoundaryCheck = useCallback(() => {
    const handler = () => checkBoundaries();
    document.addEventListener('pointerup', handler);
    return () => document.removeEventListener('pointerup', handler);
  }, [checkBoundaries]);

  return { checkBoundaries, setupBoundaryCheck };
};
